import {
    Module,
    VuexModule,
    Action,
    getModule,
    Mutation,
} from 'vuex-module-decorators';
import store from '~common-store';
import { PlantsState, PlantDevice } from '~/interfaces/eliot.interface';
import { LoadingModule } from '~common-store/modules/loading';
import apiService from '~/services/api.service';
import { FieldObject } from '~/interfaces/field.interface';
import { id, name, type, status, country, zipCode, address } from '~/helpers/fields-description';
import { ip } from '~/helpers/network-fields-description';
import { Plant } from '~/models/plant.model';
import router from '~/router';
import { ConfigModule } from '~/store/modules/config';

@Module({ dynamic: true, store, name: 'eliot', namespaced: true })
class Plants extends VuexModule implements PlantsState {

    plants: Plant[] = [] as Plant[];
    currentPlantId: string | undefined = '';
    currentGatewayId: string | undefined = '';
    currentModuleId: string | undefined = '';
    devices: PlantDevice [] = [] as PlantDevice [];

    get buildDeviceTree() {
        return (devicesList: PlantDevice[]) => {

            // Clean previous gateways
            const devices: PlantDevice[] = [];

            // Intializations
            const map = new Map();
            const l = devicesList.length;
            // Step 1: initialize map with device ids
            for (let i = 0; i < l; i++) {
                const device = devicesList[i];
                if (!device.name) {
                    device.name = 'Unnamed Module';
                }
                // Add gateway ids to the map
                if (this.isGateway(device) || this.isStandaloneDevice(device)) {
                    device.modules = []; // Override devices's childrens
                    map.set(device.id, device);
                }
            }
            // Step 2: Populate devices children with its submodules
            for (let i = 0; i < l; i++) {
                const module = devicesList[i];
                if (this.isSubModule(module)) {
                    map.get(module.gatewayId).modules.push(module);
                }
            }

            // Step 3: convert map to array
            map.forEach((module) => {
                devices.push(module);
            });

            return devices;
        };
    }

    get isGateway() {
        return (module: PlantDevice) => {
            return (typeof module.gatewayInstallation !== 'undefined'
                && (typeof module.gatewayId === 'undefined' || module.gatewayId === null)) ?
                module.gatewayInstallation : false;
        };
    }

    get isSubModule() {
        return (module: PlantDevice) => {
            return (module.gatewayInstallation === true && module.gatewayId);
        };
    }

    get isStandaloneDevice() {
        return (device: PlantDevice) => {
            return typeof device.gatewayInstallation === 'undefined' || !device.gatewayInstallation;
        };
    }

    get areGatewaysShown() {
        return (plantId: string) => {
            return this.plants.find((d) => d.id === plantId)?.areGatewaysShown;
        };
    }

    get deviceExists() {
        return (deviceId: string) => {
            return typeof this.devices.find((d) => d.macAddress === deviceId) !== 'undefined'
                || typeof this.devices.find((d) => d.id === deviceId) !== 'undefined'; // for standalone gateways
        };
    }

    get plantExists() {
        return (plantId: string) => {
            return typeof this.plants.find((p) => p.id === plantId) !== 'undefined';
        };
    }

    get gatewaysOfPlant() {
        return (plantId: string) => {
            return this.devices.filter((dev) => dev.plantId === plantId);
        };
    }

    get plantSpecFields(): FieldObject[] {
        if (this.currentPlant) {
            return [
                id(this.currentPlant.id),
                name(this.currentPlant.name),
                type(this.currentPlant.type),
                status(
                    this.currentPlant.status,
                    this.currentPlant.status === 'active' ? 'success--text' : 'error--text',
                ),
                country(this.currentPlant.country),
                zipCode(this.currentPlant.zipCode),
                address(this.currentPlant.address),
            ];
        } else {
            return [] as FieldObject[];
        }
    }

    get allowedUsers() {
        return (plantId: string) => {
            const plant = this.plants.find((p) => p.id === plantId);

            return plant?.allowedUsers;
        };
    }

    get invitedUsers() {
        return (plantId: string) => {
            const plant = this.plants.find((p) => p.id === plantId);
            return plant?.invitedUsers;
        };
    }

    get currentPlant() {
        return this.plants.find((plant) => plant.id === this.currentPlantId);
    }

    get plantNetworkFields(): FieldObject[] {
        if (this.currentPlant) {
            return [
                ip(this.currentPlant.ipAddress),
            ];
        } else {
            return [] as FieldObject[];
        }
    }

    get deviceNetworkFields() {
        return (device?: PlantDevice) => {

            if (device) {
                return [
                    ip(device.ipAddress),
                ];
            } else {
                return [] as FieldObject[];
            }
        };
    }

    get currentGateway() {
        return this.devices.find((device) => device.macAddress === this.currentGatewayId)
            || this.devices.find((device) => device.id === this.currentGatewayId); // standalone
    }

    get currentModule() {
        for (const dev of this.devices) {
            const mod = dev.modules?.find(
                (m: PlantDevice) => m.id === this.currentModuleId);
            if (mod) {
                return mod;
            }
        }
    }

    get deviceSpecFields() {
        return (device?: PlantDevice) => {
            if (device) {
                return [
                    // when it is a module it does not have mac adresse we need to take id
                    ip(device.macAddress || device.id),
                    name(device.name),
                    { key: 'Serial Number', value: device.serialNumber, type: 'string' },
                    { key: 'Device', value: device.device, type: 'string' },
                    { key: 'Device type', value: device.deviceType, type: 'string' },
                    { key: 'Hardware Version', value: device.hardwareVersion, type: 'string' },
                    { key: 'firmware', value: device.firmwareVersion, class: 'bold' },
                    { key: 'Activation date', value: device.activationDate , type: 'formattedDate' },
                    { key: 'Connection State Date', value: device.connectionStateDate, type: 'string' },
                    { key: 'Connection State', value: device.connectionState, type: 'string' },
                    {
                        key: 'Owner',
                        value: device.ownerEmail,
                        link: `/support/eliotuser/${device.ownerEmail}`,
                        type: 'internalLink',
                    },
                ];
            } else {
                return [] as FieldObject[];
            }
        };
    }


    get supportAppNameBase() {
        return ConfigModule.supportAppNameBase;
    }

    get currentGatewayModules(): PlantDevice [] {
        return this.currentGateway?.modules || [];
    }

    get currentGatewayChildren(): string [] {
        return this.currentGateway?.children || [];
    }

    get currentPlantNodes() {
        return this.plants.find((plant) => plant.id === this.currentPlantId)?.nodes;
    }

    get isPlantGatewayShown() {
        return (plantId: string) => {
            const plantToUpdate = this.plants.find((p) => p.id === plantId);
            return plantToUpdate?.areGatewaysShown;
        };
    }

    get deviceFromId() {
        return (deviceId: string) => {
            return this.devices.find((dev) => dev.id === deviceId);
        };
    }

    @Mutation
    SET_PLANTS(plants: Plant[]) {
        this.plants = plants;
    }

    @Mutation
    ADD_DEVICE(params: { device: PlantDevice, plantId?: string }) {
        // if the device we want to add already exists : replace it otherwise create new one.
        if (params.plantId) {
            params.device.plantId = params.plantId;
        }
        let existingDevice = this.devices.find((d) => d.id === params.device.id);
        if (existingDevice) {
            existingDevice = Object.assign(existingDevice, params.device);
        } else {
            this.devices.push(params.device);
        }
    }

    @Mutation
    SET_PLANT_INFO(plantInfo: Plant) {
        // update plant info and add devices to store.
        let plantToUpdate = this.plants.find((p) => p.id === plantInfo.id);
        if (plantToUpdate) {
            plantToUpdate = Object.assign(plantToUpdate);
        } else {
            plantInfo.areGatewaysShown = false;
            this.plants.push(plantInfo);
        }

    }

    @Mutation
    SET_CURRENT_DEVICE(deviceId: string) {
        this.currentGatewayId = deviceId;
    }

    @Mutation
    SET_CURRENT_DEVICE_ID(deviceId: string) {
        this.currentGatewayId = deviceId;
    }

    @Mutation
    SET_CURRENT_PLANT_ID(plantId: string) {
        this.currentPlantId = plantId;
    }

    @Mutation
    SET_CURRENT_MODULE_ID(moduleId: string) {
        this.currentModuleId = moduleId;
    }

    @Mutation
    RESET_ALL() {
        this.currentPlantId = undefined;
        this.currentGatewayId = undefined;
        this.devices = [];
        this.plants = [];
    }

    @Mutation
    RESET_PLANTS() {
        this.currentPlantId = '';
        this.plants = [];
    }

    @Mutation
    RESET_PLANT_DEVICES() {
        this.currentGatewayId = '';
        this.devices = [];
    }

    @Mutation
    UPDATE_GATEWAYS_SHOWN(plantId: string) {
        const plantToUpdate = this.plants.find((p) => p.id === plantId);
        if (plantToUpdate) {
            plantToUpdate.areGatewaysShown = !plantToUpdate.areGatewaysShown;
        }
    }

    @Mutation
    SET_FIRST_MODULE_ID(moduleId: string) {
        this.currentModuleId = moduleId;
    }

    @Action
    reset() {
        this.context.commit('RESET_ALL');
    }

    @Action
    resetPlant() {
        this.context.commit('RESET_PLANTS');
    }

    @Action
    resetPlantDevices() {
        this.context.commit('RESET_PLANT_DEVICES');
    }

    @Action
    setPlants(plants: Plant[]) {
        plants.forEach((plant) => plant.areGatewaysShown = false);
        this.context.commit('SET_PLANTS', plants);
    }

    @Action
    async getPlantInfo(plantId: string) {
        try {
            LoadingModule.showLoading(true);
            const response = await apiService.admingetplantinfo({ plant_id: plantId });
            const plantInfo = response.data.body.plant;
            let devices: PlantDevice [] = [] as PlantDevice [];
            // first build devices as needed
            if (plantInfo.topology) {
                devices = this.buildDeviceTree(plantInfo.topology.modules);
            }
            const plantModel = new Plant(plantInfo);
            // commit plant info
            this.context.commit('SET_PLANT_INFO', plantModel);

            // commit devices and add plant id to it
            devices.forEach((device) => {
                this.addDevice({ device, plantId });
            });
            LoadingModule.showLoading(false);
        } catch (error) {
            return;
        }
    }

    @Action
    async addDevice({ device, plantId }: { device: PlantDevice, plantId?: string }) {
        this.context.commit('ADD_DEVICE', { device, plantId });
    }

    @Action
    async setCurrentGatewayId(deviceId: string | undefined) {
        this.context.commit('SET_CURRENT_DEVICE_ID', deviceId);
        // set plant id corresponding to the module.
        const device = this.devices.find((dev) => dev.macAddress === deviceId)
            || this.devices.find((dev) => dev.id === deviceId);
        if (device) {
            this.context.commit('SET_CURRENT_PLANT_ID', device.plantId);
        }
    }

    @Action
    async setCurrentPlantId(plantId: string | undefined) {
        this.context.commit('SET_CURRENT_PLANT_ID', plantId);
    }

    @Action
    async clickShowGateway(plantId: string) {
        const plantToUpdate = this.plants.find((p) => p.id === plantId);

        // If we are showing gateway and we don't know the toplology of the plant we need to get it from api
        if (!plantToUpdate?.areGatewaysShown) {
            await this.getPlantDevices(plantId);
        }
        this.context.commit('UPDATE_GATEWAYS_SHOWN', plantId);
    }

    @Action
    async getPlantDevices(plantId: string) {
        try {
            LoadingModule.showLoading(true);
            const response = await apiService.admingetplantmodules({ plant_id: plantId });

            let devices: PlantDevice[] = response.data.body.modules as PlantDevice[];
            // first build devices as needed
            devices = this.buildDeviceTree(devices);

            // commit devices
            devices.forEach((device) => {
                this.addDevice({ device });
            });

            LoadingModule.showLoading(false);
        } catch (error) {
            return;
        }
    }

    @Action
    setFirstModuleId() {
        if (this.currentGateway?.modules && this.currentGateway?.modules?.length > 0) {
            this.context.commit('SET_FIRST_MODULE_ID', this.currentGateway?.modules[0].id);
        }
    }

    @Action
    setCurrentModuleId(moduleId: string) {
        return this.context.commit('SET_CURRENT_MODULE_ID', moduleId);
    }

    @Action
    async deletePlant(plantId: string) {
        try {
            LoadingModule.showLoading(true);
            await apiService.admindeleteplant({ plant_id: plantId });
            router.push({ name: `${ConfigModule.supportAppNameBase}-index` });
        } catch (error) {
            return;
        } finally {
            LoadingModule.showLoading(false);
        }
    }
}

export const EliotModule = getModule(Plants);
